import React, {useState} from "react";
import {useToggle} from "react-use";
import Alert from "react-s-alert";
import {
  Button,
  Checkbox,
  Modal,
  ModalContent,
  ModalFooterButtons,
  Text,
  styled,
} from "@reside/ui";

import {FacilityStatusEnum} from "../../../../utils/types/facility";
import {useFetchFacilitiesQuery} from "../../../../routers/AdminAppRefresh";
import {useUpdateFacilityStatusMutation} from "../../hooks";

type Props = Readonly<{
  facilityId: string;
  isInactive: boolean;
}>;

/**
 * Reside admin action to deactivate an active facility or activate an inactive one.
 */
export const FacilityStatusButton = ({facilityId, isInactive}: Props) => {
  const [isModalOpen, toggleModalOpen] = useToggle(false);
  // The status the modal was opened for. The facilities refetch flips `isInactive` before the modal closes,
  // so the modal keeps showing the action that was confirmed instead of switching to the opposite one.
  const [isActivating, setIsActivating] = useState(isInactive);
  // An inactive facility is left out of reporting unless the Reside admin opts in
  const [keepInReporting, setKeepInReporting] = useState(false);
  const {fetchFacilitiesQuery} = useFetchFacilitiesQuery();
  const {mutate: updateStatus, isLoading} = useUpdateFacilityStatusMutation();

  const action = isInactive ? "Activate" : "Deactivate";
  const modalAction = isActivating ? "Activate" : "Deactivate";

  const openModal = () => {
    setIsActivating(isInactive);
    setKeepInReporting(false);
    toggleModalOpen(true);
  };

  const onConfirm = () => {
    updateStatus({
      facilityId,
      status: isActivating
        ? FacilityStatusEnum.ACTIVE
        : FacilityStatusEnum.INACTIVE,
      ...(isActivating ? {} : {includeInReporting: keepInReporting}),
      onSuccess: async () => {
        await fetchFacilitiesQuery();
        toggleModalOpen(false);
        Alert.success(
          `Facility ${isActivating ? "activated" : "deactivated"} successfully`,
        );
      },
    });
  };

  return (
    <>
      <Button
        outline
        color={isInactive ? "success" : "danger"}
        testId="facilityStatusButton"
        onClick={openModal}
      >
        {action}
      </Button>
      <Modal
        isOpen={isModalOpen}
        onRequestClose={() => toggleModalOpen(false)}
        title={`${modalAction} Facility`}
      >
        {({closeModal}) => (
          <>
            <ModalContent>
              {isActivating ? (
                <Text as="p">
                  Do you really want to activate this facility? Users will be
                  able to see and use it again.
                </Text>
              ) : (
                <>
                  <Text as="p">
                    Do you really want to deactivate this facility?
                  </Text>
                  <Text as="p">
                    The facility will be hidden from everyone except Reside
                    admins, its settings can&apos;t be edited and no new
                    workflows can be created in it. Existing workflows stay as
                    they are. The facility can be activated again at any time.
                  </Text>
                  <KeepInReportingLabel>
                    <Checkbox
                      name="keepInReporting"
                      value="yes"
                      checked={keepInReporting}
                      onChange={() => setKeepInReporting(!keepInReporting)}
                      disabled={isLoading}
                    />
                    Keep this facility&apos;s workflows in reporting
                  </KeepInReportingLabel>
                </>
              )}
            </ModalContent>
            <ModalFooterButtons>
              <Button
                outline
                color="primary"
                onClick={closeModal}
                disabled={isLoading}
              >
                Cancel
              </Button>
              <Button
                color={isActivating ? "success" : "danger"}
                testId="confirmFacilityStatusButton"
                onClick={onConfirm}
                disabled={isLoading}
              >
                {isLoading ? "Saving..." : modalAction}
              </Button>
            </ModalFooterButtons>
          </>
        )}
      </Modal>
    </>
  );
};

const KeepInReportingLabel = styled.label`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-top: 16px;
  cursor: pointer;
`;
